// src/components/routing/ProtectedRoute.tsx
import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useEntitlement } from '../../contexts/EntitlementContext';

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { isAuthenticated, permissoes, role, isInitialized, hasPermission } = useAuth();
  const { isAreaEnabled, modules, status: entitlementStatus } = useEntitlement();
  const [shouldRedirect, setShouldRedirect] = useState<string | null>(null);
  const location = useLocation(); // rastrea a rota atual

  // INVARIANTE: o destino do redirect de fallback ('/home') NUNCA pode ser uma
  // rota gateada (em routePermissions com área de módulo). Hoje '/home' não está
  // aqui e a Home trata "Nenhum módulo disponível" graciosamente. Se um dia uma
  // rota-destino entrar neste mapa com área gateada, dois caminhos gateados
  // podem entrar em loop de redirect — manter '/home' fora deste mapa.
  const routePermissions: { [key: string]: string } = {
    '/usuarios': 'modulo.gestao_usuarios',
    '/requisicoes': 'modulo.requisicoes',
    '/compras': 'modulo.gestao_compras',
    '/integracoes': 'modulo.integracoes',
    '/parametrizacao': 'modulo.parametrizacao',
    '/conferencia': 'modulo.gestao_conferencias', // Ajustado para nova permissão
    '/contrato': 'modulo.contrato_bluelt',
    // Só o path exato entra aqui — `/bluebi/<slug>` (o painel em tela cheia) confere os
    // mesmos dois eixos dentro do próprio PainelView.
    '/bluebi': 'modulo.bluebi',
  };

  const currentPath = location.pathname;

  useEffect(() => {
    if (!isInitialized) return;

    if (!isAuthenticated) {
      setShouldRedirect('/login');
    } else if (currentPath in routePermissions && !hasPermission(routePermissions[currentPath])) {
      setShouldRedirect('/home');
    } else if (
      currentPath in routePermissions &&
      // Gate de módulo (sem bypass de MASTER): área = sufixo de `modulo.<area>`.
      // Cobre acesso por URL direta, inclusive ROLE_MASTER. Fail-open enquanto
      // entitlement não resolve, então re-roda quando `modules`/`status` mudam.
      !isAreaEnabled(routePermissions[currentPath].split('.')[1])
    ) {
      setShouldRedirect('/home');
    } else {
      setShouldRedirect(null);
    }
    // hasPermission/isAreaEnabled omitidos de propósito: hasPermission não é
    // memoizado no AuthContext (recriado por render). A reavaliação do gate de
    // módulo quando o entitlement resolve é coberta por `modules`/`entitlementStatus`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, permissoes, role, isInitialized, currentPath, modules, entitlementStatus]);

  if (shouldRedirect) {
    return <Navigate to={shouldRedirect} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;